import assert from "owa-assert/lib/assert";
import { CoprincipalMailboxRank, getAccountKeyForMailboxInfo } from "owa-client-types";
import { errorThatWillCauseAlert, trace } from "owa-trace";
import type { TraceErrorObject } from "owa-analytics-types";
import addCoprincipalAccountMutator from "../actions/addCoprincipalAccount";
import onCoprincipalAccountAdded from "../actions/onCoprincipalAccountAdded";
import { updateCoprincipalUserIdentities } from "./coprincipalUserIdentities";
import { interceptAndAlertOnMailboxInfoChanges } from "./interceptAndAlertOnMailboxInfoChanges";
import { updateMailboxInfo } from "./updateMailboxInfo";
import type { CoprincipalAccountSource } from "../store/schema/AccountSourceList";
import { updateCoprincipalAccountContracts } from "./updateCoprincipalAccountContracts";
import { logUsageFromAccounts } from "owa-account-analytics";
import { scrubForPii, ScrubType } from "owa-config/lib/scrubForPii";
import getCoprincipalAccountForMailboxInfo from "./getCoprincipalAccountForMailboxInfo";
import tryGetGlobalSettingsAccountMailboxInfo from "../selectors/tryGetGlobalSettingsAccountMailboxInfo";

/**
 * Gets the diagnostic data for the duplicate accountKey error
 * @param existingAccount Existing account that has the accountKey value
 * @param newAccount New account that has the same accountKey value
 * @returns Diagnostic data for the duplicate accountKey error
 */
function getDuplicateAccountKeyDiagnosticData(existingAccount: CoprincipalAccountSource, newAccount: CoprincipalAccountSource) {
    return {
        type: newAccount.sourceType,
        existingType: existingAccount.sourceType,
        areSameType: newAccount.sourceType === existingAccount.sourceType,
        areSameSmtp: newAccount.mailboxInfo.mailboxSmtpAddress ===
            existingAccount.mailboxInfo.mailboxSmtpAddress,
        areSameUid: newAccount.mailboxInfo.userIdentity === existingAccount.mailboxInfo.userIdentity,
        areSamePersistenceId: newAccount.persistenceId === existingAccount.persistenceId,
    };
}

/**
 * Checks to make sure that the accountKey value of the coprincipal account is unique. If the accountKey
 * value is not unique this method will throw
 * @param accountSource Coprincipal account that is to be added
 */
function checkThatAccountKeyIsUnique(accountSource: CoprincipalAccountSource) {
    const accountKey = getAccountKeyForMailboxInfo(accountSource.mailboxInfo);
    
    if (accountKey !== accountSource.sourceId) {
        // The accountKey must always be the sourceId of the coprincipal account.
        errorThatWillCauseAlert("AddAccount-AccountKeyMismatch");
    }
    
    const existingAccount = getCoprincipalAccountForMailboxInfo(accountSource.mailboxInfo);
    if (!!existingAccount) {
        // There is already a coprincipal account with the same accountKey in the store. This account
        // should not have be added to the store and we should alert the system that there is an
        // error in the build.
        const error: TraceErrorObject = new Error("DuplicateCoprincipalAccountKey");
        error.additionalInfo = getDuplicateAccountKeyDiagnosticData(existingAccount, accountSource);
        errorThatWillCauseAlert("DuplicateCoprincipalAccountKey", error);
    }
}

/**
 * Preforms validation on the account source and then adds the account
 * @param accountSource
 */
export default function addCoprincipalAccount(accountSource: CoprincipalAccountSource): void {
    const existingAccount = getCoprincipalAccountForMailboxInfo(accountSource.mailboxInfo);
    if (existingAccount != undefined) {
        assert(false, "Found duplicated account in the store.", () => JSON.stringify(getDuplicateAccountKeyDiagnosticData(existingAccount, accountSource)));
    }
    
    assert(accountSource.mailboxRank === CoprincipalMailboxRank, "Must be a Coprincipal account");
    
    trace.info(`[source-list-store] Adding coprincipal account with boot state ${accountSource.bootState}`);
    logUsageFromAccounts("AddCoprincipalAccount", tryGetGlobalSettingsAccountMailboxInfo(false), {
        sourceId: scrubForPii(accountSource.sourceId, true, ScrubType.Email),
        persistenceId: scrubForPii(accountSource.persistenceId, true, ScrubType.Email),
        sourceType: accountSource.sourceType,
        callstack: new Error("AddCoprincipalAccount").stack,
        puid: accountSource.authenticatingUserIdentifiers?.puid,
        oid: accountSource.authenticatingUserIdentifiers?.objectId,
        tenantId: accountSource.authenticatingUserIdentifiers?.tenantId,
        hasUpn: !!accountSource.authenticatingUserIdentifiers?.userPrincipalName,
    });
    
    accountSource = updateMailboxInfo(accountSource) as CoprincipalAccountSource;
    accountSource = updateCoprincipalAccountContracts(accountSource);
    checkThatAccountKeyIsUnique(accountSource);
    addCoprincipalAccountMutator(accountSource);
    
    interceptAndAlertOnMailboxInfoChanges(accountSource.sourceId);
    updateCoprincipalUserIdentities();
    onCoprincipalAccountAdded(accountSource);
}
